import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { mainServiceAppURL } from '../../../redux/services/defines';

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  fullName: string;
  companyID: string;
  role: 'admin' | 'user';
  isSuperAdmin: boolean;
  isActive: boolean;
  emailVerified: boolean;
  onboardingCompleted: boolean;
  companyName?: string;
  jobTitle?: string;
  companySize?: string;
  industry?: string;
  useCase?: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
}

type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

interface SetupInput {
  username: string;
  email: string;
  fullName: string;
  password: string;
  companyName?: string;
}

interface AuthContextValue {
  status: AuthStatus;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsSetup: boolean;
  user: AuthUser | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  /** @deprecated Setup is handled via main-service onboarding. This is a no-op. */
  setup: (input: SetupInput) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  rememberMe: boolean;
  setRememberMe: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextValue>({
  status: 'loading',
  isLoading: true,
  isAuthenticated: false,
  needsSetup: false,
  user: null,
  login: async () => {
    throw new Error('Auth context not initialized');
  },
  setup: async () => {
    throw new Error('Setup is no longer supported. Use main-service sign-up.');
  },
  logout: async () => {
    throw new Error('Auth context not initialized');
  },
  refresh: async () => {
    throw new Error('Auth context not initialized');
  },
  rememberMe: false,
  setRememberMe: () => {},
});

const parseJson = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const resolveErrorMessage = (payload: any, fallback: string): string => {
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }
  if (Array.isArray(payload?.message) && payload.message[0]) {
    return String(payload.message[0]);
  }
  return fallback;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [rememberMe, setRememberMe] = useState(false);

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const response = await fetch(`${mainServiceAppURL}/auth/verify`, {
        method: 'GET',
        credentials: 'include',
      });
      if (response.ok) {
        const payload = await parseJson(response);
        if (payload?.authenticated && payload?.user) {
          setUser(payload.user as AuthUser);
          setStatus('authenticated');
          return;
        }
      }
      setUser(null);
      setStatus('unauthenticated');
    } catch {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string, remember: boolean = rememberMe) => {
      const response = await fetch(`${mainServiceAppURL}/login/native`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, rememberMe: remember }),
      });

      const payload = await parseJson(response);
      if (!response.ok) {
        throw new Error(resolveErrorMessage(payload, 'Login failed'));
      }

      // Cookies are set by main-service — load user via verify
      await refresh();
    },
    [rememberMe, refresh],
  );

  const logout = useCallback(async () => {
    try {
      await fetch(`${mainServiceAppURL}/logout/native`, {
        method: 'GET',
        credentials: 'include',
      });
    } catch {
      // Ignore errors, clear state regardless
    }
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const setup = useCallback(async (_input: SetupInput) => {
    throw new Error('Setup is no longer supported. Users are managed via main-service.');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      needsSetup: false,
      user,
      login,
      setup,
      logout,
      refresh,
      rememberMe,
      setRememberMe,
    }),
    [status, user, login, setup, logout, refresh, rememberMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
